import React, { useEffect, useId, useRef, useState } from 'react';
import ReactCrop, {
  centerCrop,
  convertToPixelCrop,
  makeAspectCrop,
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
  Button,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useController } from 'react-hook-form';
import { useTranslate } from 'react-admin';
import { resolveMediaUrl } from '../../utils/mediaUrl';

/** Varsayılan: anasayfa dikey kart 4:5 (1080×1350) */
const DEFAULT_ASPECT = 4 / 5;

export const EditableImage = ({
  source,
  record,
  width = 216,
  height = 270,
  label,
  aspect = DEFAULT_ASPECT,
  hint,
  cropTitle,
  cropDesc,
}) => {
  const translate = useTranslate();
  const inputId = useId();
  const fileInputRef = useRef(null);
  const imageRef = useRef(null);
  const aspectRatio = aspect > 0 ? aspect : DEFAULT_ASPECT;

  const displayHint =
    hint ||
    translate(
      'form.poster_crop_hint',
      'Poster görselleri 4:5 (1080×1350) önerilir. Kaydetmeden önce görsel önizlemesini kontrol edin.',
    );
  const dialogTitle =
    cropTitle ||
    translate('form.crop_image_title', 'Görseli kırp (4:5)');
  const dialogDesc =
    cropDesc ||
    translate(
      'form.crop_image_desc',
      'Kırpma alanı 4:5 oranında kilitli. Alanı sürükleyip boyutunu ayarlayabilirsiniz.',
    );

  const { field } = useController({
    name: source,
    defaultValue: record?.[source] ?? null,
    shouldUnregister: false,
  });

  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState({});
  const [croppedImageUrl, setCroppedImageUrl] = useState(null);
  const [cropping, setCropping] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Edit: record API'den geldiginde form alanini senkronize et
  useEffect(() => {
    const fromRecord = record?.[source];
    if (!fromRecord?.src) return;
    // Yeni secilen dosya / blob beklerken kaydi ezme
    if (field.value?.rawFile) return;
    if (typeof field.value?.src === 'string' && field.value.src.startsWith('blob:')) {
      return;
    }
    field.onChange(fromRecord);
    // field bilerek deps disinda — sadece record degisince senkronize
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record, source]);

  const previewSrcRaw =
    croppedImageUrl ||
    field.value?.src ||
    (typeof field.value === 'string' ? field.value : null) ||
    record?.[source]?.src ||
    null;
  const previewSrc =
    previewSrcRaw &&
    !String(previewSrcRaw).startsWith('blob:') &&
    !String(previewSrcRaw).startsWith('data:')
      ? resolveMediaUrl(previewSrcRaw)
      : previewSrcRaw;

  const onSelectFile = (e) => {
    if (!e.target.files?.length) return;
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setSrc(reader.result);
      setCrop({});
      setModalOpen(true);
    });
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const getCroppedImg = (image, percentCrop) => {
    const pixelCrop = convertToPixelCrop(percentCrop, image.width, image.height);
    if (!pixelCrop.width || !pixelCrop.height) {
      return Promise.reject(new Error('Empty crop'));
    }

    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = Math.max(1, Math.round(pixelCrop.width * scaleX));
    canvas.height = Math.max(1, Math.round(pixelCrop.height * scaleY));
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      pixelCrop.x * scaleX,
      pixelCrop.y * scaleY,
      pixelCrop.width * scaleX,
      pixelCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height,
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          resolve(null);
          return;
        }
        blob.name = 'cropped.jpg';
        const file = new File([blob], 'cropped.jpg', { type: 'image/jpeg' });
        const previewUrl = URL.createObjectURL(blob);
        resolve({ file, previewUrl });
      }, 'image/jpeg');
    });
  };

  const onImageLoaded = (e) => {
    const { width: imgWidth, height: imgHeight } = e.currentTarget;
    const initialCrop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        aspectRatio,
        imgWidth,
        imgHeight,
      ),
      imgWidth,
      imgHeight,
    );
    setCrop(initialCrop);
  };

  const applyCrop = async () => {
    if (!imageRef.current || !crop?.width) return;
    setCropping(true);
    try {
      const res = await getCroppedImg(imageRef.current, crop);
      if (res?.file) {
        setCroppedImageUrl(res.previewUrl);
        field.onChange({
          src: res.previewUrl,
          rawFile: res.file,
          title: res.file.name,
        });
      }
    } catch (err) {
      console.error('Crop failed:', err);
    } finally {
      setCropping(false);
      setModalOpen(false);
    }
  };

  const clearImage = () => {
    setCroppedImageUrl(null);
    setSrc(null);
    field.onChange(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const closeModal = () => {
    setModalOpen(false);
    setSrc(null);
  };

  return (
    <Box sx={{ my: 1 }}>
      {label ? (
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {label}
        </Typography>
      ) : null}

      {previewSrc ? (
        <Box sx={{ mb: 1 }}>
          <img
            src={previewSrc}
            alt="Preview"
            style={{
              width,
              height,
              objectFit: 'cover',
              display: 'block',
              borderRadius: 4,
            }}
          />
        </Box>
      ) : null}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={openFilePicker}
          disabled={cropping}
        >
          {previewSrc ? translate('form.change_image', 'Görseli Değiştir') : translate('form.add_image', 'Görsel Ekle')}
        </Button>
        {previewSrc && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={clearImage}
            disabled={cropping}
          >
            {translate('form.remove', 'Kaldır')}
          </Button>
        )}
      </Box>

      <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
        {displayHint}
      </Typography>

      <input
        ref={fileInputRef}
        id={inputId}
        accept="image/*"
        type="file"
        style={{ display: 'none' }}
        onChange={onSelectFile}
      />

      <Dialog
        open={modalOpen}
        onClose={cropping ? undefined : closeModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {dialogDesc}
          </Typography>
          {src ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                bgcolor: '#111',
                borderRadius: 1,
                p: 1,
              }}
            >
              <ReactCrop
                crop={crop}
                aspect={aspectRatio}
                ruleOfThirds
                onChange={setCrop}
              >
                <img
                  ref={imageRef}
                  src={src}
                  alt="Crop preview"
                  onLoad={onImageLoaded}
                  style={{ maxWidth: '100%', maxHeight: '70vh' }}
                />
              </ReactCrop>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} disabled={cropping}>
            {translate('ra.action.cancel', 'İptal')}
          </Button>
          <Button
            variant="contained"
            onClick={applyCrop}
            disabled={cropping || !crop?.width}
          >
            {cropping ? translate('form.cropping', 'Hazırlanıyor…') : translate('form.apply_crop', 'Kırp ve uygula')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
